import {
  Box,
  Button,
  IconButton,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatFlowCaption from 'components/@home/@settings/Tabs/ChatFlowCaption';
import chatFlowIntegrationFormik from 'components/@home/@settings/Tabs/chatFlowIntegrationFormik';
import connector from 'components/@home/@settings/Tabs/connector';
import withContext from 'components/@home/@settings/Tabs/withContext';
import TextField from 'components/formik/TextField';
import FormikChipInput from 'components/formik/ChipInput';
import { Field } from 'formik';
import ArrowBackIcon from 'mdi-react/ArrowBackIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import { func, object } from 'prop-types';
import React, { useMemo } from 'react';
import { I18n } from 'utils/i18n';
import GoogleSheetsConfig from './integrations/GoogleSheetsConfig';
import MicrosoftExcelConfig from './integrations/MicrosoftExcelConfig';
import SlackConfig from './integrations/SlackConfig';
import EmailConfig from './integrations/EmailConfig';
import CustomWebhookConfig from './integrations/CustomWebhookConfig';

const getStyles = theme => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'column',
    borderLeft: `solid 1px ${theme.palette.secondary.extraLight}`,
  },
  title: {
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontStretch: 'normal',
    color: theme.palette.primary.ultraDark,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  close: {
    color: theme.palette.primary.ultraDark,
  },
  body: {
    flexGrow: 1,
    overflow: 'auto',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  typeSelect: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
});

const integrationTypes = [
  { value: 'google_sheets', label: I18n.t('ChatFlowSettings.Integration Types.google_sheets') },
  { value: 'excel', label: I18n.t('ChatFlowSettings.Integration Types.excel') },
  { value: 'email', label: I18n.t('ChatFlowSettings.Integration Types.email') },
  { value: 'custom_webhook', label: I18n.t('ChatFlowSettings.Integration Types.custom_webhook') },
];

const ChatFlowIntegration = ({
  handleSubmit,
  context,
  values,
  setFieldValue,
  errors,
  submitCount,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { type } = values;

  const renderConfigurationFields = () => {
    switch (type) {
      case 'google_sheets':
        return (
          <GoogleSheetsConfig
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            submitCount={submitCount}
          />
        );
      case 'excel':
        return (
          <MicrosoftExcelConfig
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            submitCount={submitCount}
          />
        );
      case 'slack':
        return (
          <SlackConfig
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            submitCount={submitCount}
          />
        );
      case 'email':
        return (
          <EmailConfig
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            submitCount={submitCount}
          />
        );
      case 'zapier':
        return (
          <CustomWebhookConfig
            isZapier
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            submitCount={submitCount}
          />
        );
      case 'custom_webhook':
        return (
          <CustomWebhookConfig
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            submitCount={submitCount}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={styles.root}>
      <Box component="header" sx={styles.header}>
        <Box sx={styles.headerLeft}>
          {context.length > 1 && (
            <IconButton
              sx={styles.close}
              edge="start"
              aria-label="close"
              onClick={context.pop}
              size="large"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography size="small" sx={styles.title}>
            {I18n.t('ChatFlowSettings.Integration')}
          </Typography>
        </Box>
        <div>
          <IconButton
            sx={styles.close}
            edge="end"
            aria-label="close"
            onClick={context.clear}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </Box>
      <Box component="section" sx={styles.body}>
        <ChatFlowCaption section="Integration" />

        <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Display name')}</Typography>
        <Field
          component={TextField}
          name="name"
          placeholder={I18n.t('ChatFlowSettings.Display name')}
          size="small"
          required
        />

        <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Integration type')}</Typography>
        <FormControl fullWidth sx={styles.typeSelect} size="small">
          <InputLabel>{I18n.t('ChatFlowSettings.Select integration type')}</InputLabel>
          <Select
            value={type || ''}
            onChange={e => {
              const newType = e.target.value;
              setFieldValue('type', newType);
              const defaultConfigurations = {
                email: {
                  recipients: [],
                },
                custom_webhook: {
                  headers: [],
                },
              };
              setFieldValue('configuration', defaultConfigurations[newType] || {});
            }}
            label={I18n.t('ChatFlowSettings.Select integration type')}
          >
            {integrationTypes.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {type && (
          <>
            <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Configuration')}</Typography>
            {renderConfigurationFields()}
          </>
        )}

        <Typography sx={styles.heading}>
          {I18n.t('ChatFlowSettings.Error notifications')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
          {I18n.t('ChatFlowSettings.Error notification emails description')}
        </Typography>
        <Field
          component={FormikChipInput}
          name="failureNotificationEmails"
          blurBehavior="add"
          placeholder={I18n.t('ChatFlowSettings.Email')}
        />
      </Box>
      <Box component="footer" sx={styles.footer}>
        <div />
        <Button variant="contained" color="primary" type="submit">
          {I18n.t('ChatFlowSettings.Save')}
        </Button>
      </Box>
    </Box>
  );
};

ChatFlowIntegration.propTypes = {
  handleSubmit: func.isRequired,
  context: object.isRequired,
  values: object.isRequired,
  setFieldValue: func.isRequired,
  errors: object.isRequired,
  submitCount: func.isRequired,
};

export default connector(withContext(chatFlowIntegrationFormik(ChatFlowIntegration)));
